/*
 * Custom-calculator product page — wall measurements, pricing, add to cart.
 * Mirrors the AI generator calculator math (metric internally, imperial display).
 */
(() => {
  const CM_PER_INCH = 2.54;
  const FT2_PER_M2 = 10.7639;

  const toNum = (value, fallback = 0) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  };

  /**
   * @param {HTMLElement} root
   */
  function initWallCalculator(root) {
    if (root.dataset.wallCalculatorReady) return;
    root.dataset.wallCalculatorReady = 'true';

    const widthInput = root.querySelector('[data-wall-width]');
    const heightInput = root.querySelector('[data-wall-height]');
    const widthMarginText = root.querySelector('[data-wall-width-margin]');
    const heightMarginText = root.querySelector('[data-wall-height-margin]');
    const widthTotal = root.querySelector('[data-wall-width-total]');
    const heightTotal = root.querySelector('[data-wall-height-total]');
    const installCheckbox = root.querySelector('[data-wall-install]');
    const removeMargin = root.querySelector('[data-wall-remove-margin]');
    const installRegions = root.querySelector('[data-wall-install-regions]');
    const regionSelect = root.querySelector('[data-wall-region]');
    const messageBox = root.querySelector('[data-wall-message]');
    const unitButtons = Array.from(root.querySelectorAll('[data-wall-unit]'));
    const unitLabels = Array.from(root.querySelectorAll('[data-wall-unit-label]'));
    const areaUnitEls = Array.from(root.querySelectorAll('[data-wall-area-unit]'));

    let baseUnitPrice = toNum(root.dataset.baseUnitPrice, 0);
    const pricePerSqm = toNum(root.dataset.pricePerSqm, 0);
    if (pricePerSqm > 0) baseUnitPrice = pricePerSqm / 100;
    const installVariantId = root.dataset.installVariantId || '';
    const fallbackVariantId = root.dataset.variantId || '';
    const productTitle = root.dataset.productTitle || '';
    const defaultBleed = toNum(root.dataset.defaultBleed, 5);
    const panelWidth = toNum(root.dataset.panelWidth, 106);
    const minArea = toNum(root.dataset.minArea, 1);
    const installRate = toNum(root.dataset.installRate, 250);
    const currencyIso = (root.dataset.currencyIso || 'ZAR').toUpperCase();
    const locale = root.dataset.locale || document.documentElement.lang || 'en';

    let unit = root.dataset.defaultUnit === 'imperial' ? 'imperial' : 'metric';
    let isSubmitting = false;
    let calcTimer = 0;

    const scheduleCalculate = () => {
      window.clearTimeout(calcTimer);
      calcTimer = window.setTimeout(calculate, 80);
    };

    const productForm =
      document.querySelector('product-form form') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]');

    const addButton =
      productForm?.querySelector('button[name="add"]') ||
      productForm?.querySelector('button[type="submit"]');

    if (!productForm || !addButton) {
      console.warn('Wall calculator: product form or add button not found');
    }

    const lengthUnit = () => (unit === 'imperial' ? 'in' : 'cm');
    const areaUnit = () => (unit === 'imperial' ? 'ft²' : 'm²');
    const toCm = (value) => (unit === 'imperial' ? value * CM_PER_INCH : value);
    const fromCm = (cm) => (unit === 'imperial' ? cm / CM_PER_INCH : cm);
    const roundDisp = (value) => (unit === 'imperial' ? Math.round(value * 10) / 10 : Math.round(value));
    const areaDisp = (m2) => (unit === 'imperial' ? m2 * FT2_PER_M2 : m2);

    const setTextAll = (selector, text) => {
      root.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
      });
    };

    const formatPrice = (amountMajor) => {
      if (!Number.isFinite(amountMajor) || amountMajor <= 0) return '—';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyIso,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amountMajor);
      } catch {
        return String(Math.round(amountMajor));
      }
    };

    const setMessage = (text) => {
      if (messageBox) messageBox.textContent = text || '';
    };

    const syncUnitUI = () => {
      unitButtons.forEach((btn) => {
        const active = btn.dataset.wallUnit === unit;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
      unitLabels.forEach((el) => {
        el.textContent = lengthUnit();
      });
      areaUnitEls.forEach((el) => {
        el.textContent = areaUnit();
      });
    };

    const setUnit = (next) => {
      if ((next !== 'metric' && next !== 'imperial') || next === unit) return;
      const prev = unit;
      unit = next;
      [widthInput, heightInput].forEach((input) => {
        if (!input || input.value === '') return;
        const value = toNum(input.value, 0);
        if (!value) return;
        const cm = prev === 'imperial' ? value * CM_PER_INCH : value;
        input.value = String(roundDisp(fromCm(cm)));
      });
      syncUnitUI();
      calculate();
    };

    const getVariantId = () => {
      const variantInput = productForm?.querySelector('input[name="id"]');
      if (variantInput?.value) return variantInput.value;
      return fallbackVariantId;
    };

    const calculate = () => {
      const widthDisp = toNum(widthInput?.value, 0);
      const heightDisp = toNum(heightInput?.value, 0);
      const widthCm = widthDisp > 0 ? toCm(widthDisp) : 0;
      const heightCm = heightDisp > 0 ? toCm(heightDisp) : 0;
      const bleedCm = removeMargin?.checked ? 0 : defaultBleed;
      const printWidthCm = widthCm > 0 ? widthCm + bleedCm : 0;
      const printHeightCm = heightCm > 0 ? heightCm + bleedCm : 0;
      const lu = lengthUnit();

      const fmtLen = (cm) => `${roundDisp(fromCm(cm))} ${lu}`;
      if (widthTotal) widthTotal.textContent = fmtLen(printWidthCm);
      if (heightTotal) heightTotal.textContent = fmtLen(printHeightCm);

      const marginDisp = roundDisp(fromCm(defaultBleed));
      const marginLabel =
        bleedCm === 0 ? 'Extra margin removed' : `${marginDisp}${lu} extra margin included`;
      if (widthMarginText) widthMarginText.textContent = marginLabel;
      if (heightMarginText) heightMarginText.textContent = marginLabel;

      const actualAreaM2 = (printWidthCm / 100) * (printHeightCm / 100);
      const units = Math.round(Math.max(minArea, actualAreaM2) * 100);
      const chargeableAreaM2 = units / 100;
      const panels = printWidthCm > 0 ? Math.ceil(printWidthCm / panelWidth) : 0;

      setTextAll('[data-wall-area]', areaDisp(actualAreaM2).toFixed(2));
      setTextAll('[data-wall-chargeable]', areaDisp(chargeableAreaM2).toFixed(2));
      const printSizeText =
        printWidthCm > 0
          ? `${roundDisp(fromCm(printWidthCm))} × ${roundDisp(fromCm(printHeightCm))} ${lu}`
          : '—';
      setTextAll('[data-wall-print-size]', printSizeText);
      setTextAll('[data-wall-panels]', String(panels));

      const minApplied = actualAreaM2 > 0 && actualAreaM2 < minArea;
      root.querySelectorAll('[data-wall-min-note]').forEach((el) => {
        el.hidden = !minApplied;
        if (minApplied) {
          el.textContent = `Minimum ${areaDisp(minArea).toFixed(2)} ${areaUnit()} charge applied`;
        }
      });

      let price = 0;
      if (baseUnitPrice > 0) {
        price = chargeableAreaM2 * (baseUnitPrice * 100);
        if (installCheckbox?.checked) price += chargeableAreaM2 * installRate;
      }
      setTextAll('[data-wall-price]', baseUnitPrice > 0 ? formatPrice(price) : '—');

      return {
        widthDisp,
        heightDisp,
        widthCm,
        heightCm,
        bleedCm,
        printWidthCm,
        printHeightCm,
        actualAreaM2,
        chargeableAreaM2,
        units,
        panels,
        install: !!installCheckbox?.checked,
        installRegion: regionSelect?.value || '',
        lengthUnit: lu,
        areaUnit: areaUnit(),
        minApplied,
      };
    };

    const addToCart = async () => {
      if (!productForm || !addButton || isSubmitting) return;

      const result = calculate();
      setMessage('');

      if (!result.widthDisp || !result.heightDisp) {
        setMessage('Please enter wall width and height');
        return;
      }

      if (result.install && !result.installRegion) {
        setMessage('Please select a region');
        return;
      }

      const variantId = getVariantId();
      const lu = result.lengthUnit;
      const au = result.areaUnit;

      const wallpaperProperties = {
        'Wall Width': `${result.widthDisp} ${lu}`,
        'Wall Height': `${result.heightDisp} ${lu}`,
        'Bleed Included': `${result.bleedCm} cm`,
        'Total Size': `${roundDisp(fromCm(result.printWidthCm))} × ${roundDisp(fromCm(result.printHeightCm))} ${lu}`,
        Area: `${areaDisp(result.actualAreaM2).toFixed(2)} ${au}`,
        Panels: `${result.panels} ±`,
        Installation: result.install ? 'Yes' : 'No',
        'Installation Region': result.install ? result.installRegion : 'N/A',
        'Minimum Charge Applied': result.minApplied
          ? `Yes (${areaDisp(minArea).toFixed(2)} ${au} minimum)`
          : 'No',
      };

      const items = [
        {
          id: Number(variantId),
          quantity: Math.max(1, result.units),
          properties: wallpaperProperties,
        },
      ];

      if (result.install && installVariantId) {
        items.push({
          id: Number(installVariantId),
          quantity: Math.max(1, result.units),
          properties: {
            'Linked To': productTitle,
            Area: `${areaDisp(result.chargeableAreaM2).toFixed(2)} ${au}`,
            'Installation Region': result.installRegion,
          },
        });
      }

      try {
        isSubmitting = true;
        addButton.disabled = true;

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) throw new Error('Add to cart failed');

        window.location.assign('/cart');
      } catch (error) {
        console.error(error);
        setMessage('Could not add to cart');
        addButton.disabled = false;
        isSubmitting = false;
      }
    };

    unitButtons.forEach((btn) =>
      btn.addEventListener('click', () => setUnit(btn.dataset.wallUnit))
    );

    installCheckbox?.addEventListener('change', () => {
      if (installRegions) installRegions.hidden = !installCheckbox.checked;
      if (!installCheckbox.checked && regionSelect) regionSelect.value = '';
      calculate();
    });

    removeMargin?.addEventListener('change', scheduleCalculate);
    regionSelect?.addEventListener('change', scheduleCalculate);
    widthInput?.addEventListener('input', scheduleCalculate);
    heightInput?.addEventListener('input', scheduleCalculate);

    if (addButton) {
      addButton.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          addToCart();
        },
        true
      );
    }

    productForm?.addEventListener(
      'submit',
      (e) => {
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );

    syncUnitUI();
    calculate();
  }

  const boot = () => {
    document.querySelectorAll('[data-wall-calculator]').forEach((root) => {
      initWallCalculator(/** @type {HTMLElement} */ (root));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
